/**
 * Commission & TDS compliance — GSTR-7 prep, Form 16A certs, 26AS matching.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Download, RefreshCw, FileText, Scale, Receipt, Loader2, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import AgentCommissionSharingRoster from '../../components/admin/compliance/AgentCommissionSharingRoster';
import CompliancePeriodBar, { currentFy } from '../../components/admin/compliance/CompliancePeriodBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const SUPPLIERS = ['tripjack', 'riya', 'offline'];
const TABS = ['Summary', 'Agent sharing', 'GSTR-7', 'Form 16A', '26AS match'];
const DEFAULT_FY = currentFy();

function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function SourceBadge({ meta }) {
  if (!meta) return null;
  return (
    <p className="text-[10px] text-gray-500 mt-2" data-testid="compliance-source-badge">
      Source: <strong>{meta.data_source}</strong>
      {meta.period && <> · {meta.period.toUpperCase()}</>}
      {meta.date_from && <> · {meta.date_from.slice(0, 10)} – {meta.date_to?.slice(0, 10)}</>}
      {meta.row_count != null && <> · {meta.row_count} rows</>}
      {meta.truncated && ' · truncated'}
    </p>
  );
}

function buildParams(period, fy) {
  const params = { period };
  if (period === 'fy') params.fy = fy;
  return params;
}

export default function CommissionTdsReports() {
  const [tab, setTab] = useState('Summary');
  const [period, setPeriod] = useState('mtd');
  const [fy, setFy] = useState(DEFAULT_FY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [settings, setSettings] = useState(null);
  const [summary, setSummary] = useState(null);
  const [gstr7, setGstr7] = useState(null);
  const [certs, setCerts] = useState([]);
  const [certSupplier, setCertSupplier] = useState('tripjack');
  const [recon26, setRecon26] = useState([]);
  const [agentId, setAgentId] = useState('');
  const [agentStmt, setAgentStmt] = useState(null);

  const [certForm, setCertForm] = useState({
    supplier: 'tripjack',
    fy: DEFAULT_FY,
    quarter: 'Q1',
    certificate_number: '',
    certificate_date: '',
    gross_commission: '',
    tds_deducted: '',
    pan: '',
  });

  const [asForm, setAsForm] = useState({
    fy: DEFAULT_FY,
    quarter: 'Q1',
    pan: '',
    form26as_tds_amount: '',
  });

  const loadSettings = useCallback(async () => {
    const r = await axios.get(`${API}/settings/commission-tds`, { withCredentials: true });
    setSettings(r.data?.settings || {});
  }, []);

  const loadSummary = useCallback(async () => {
    const r = await axios.get(`${API}/reports/commission-tds`, {
      withCredentials: true,
      params: buildParams(period, fy),
    });
    setSummary(r.data);
  }, [period, fy]);

  const loadGstr7 = useCallback(async () => {
    const r = await axios.get(`${API}/reports/tds-gstr7`, {
      withCredentials: true,
      params: buildParams(period === 'mtd' ? 'fy' : period, fy),
    });
    setGstr7(r.data);
  }, [period, fy]);

  const loadCerts = useCallback(async () => {
    const r = await axios.get(`${API}/suppliers/${encodeURIComponent(certSupplier)}/tds-certificates`, {
      withCredentials: true,
    });
    setCerts(r.data?.certificates || []);
  }, [certSupplier]);

  const load26as = useCallback(async () => {
    const r = await axios.get(`${API}/tds/26as/reconciliation`, {
      withCredentials: true,
      params: period === 'fy' && fy ? { fy } : {},
    });
    setRecon26(r.data?.rows || []);
  }, [period, fy]);

  const refresh = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      await loadSettings();
      if (tab === 'Summary') await loadSummary();
      if (tab === 'GSTR-7') await loadGstr7();
      if (tab === 'Form 16A') await loadCerts();
      if (tab === '26AS match') await load26as();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Load failed');
    } finally {
      setBusy(false);
    }
  }, [tab, loadSettings, loadSummary, loadGstr7, loadCerts, load26as]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    if (tab === 'Form 16A') loadCerts();
  }, [certSupplier, tab, loadCerts]);

  const downloadCsv = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await axios.get(`${API}/reports/commission-tds/csv`, {
        withCredentials: true,
        params: buildParams(period, fy),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `commission_tds_${period}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr('CSV download failed');
    } finally {
      setBusy(false);
    }
  };

  const runBackfill = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await axios.post(`${API}/commission/backfill`, null, {
        withCredentials: true,
        params: { days: 365, limit: 5000 },
      });
      alert(`Backfill complete: ${r.data?.stamped ?? 0} bookings stamped`);
      await refresh();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Backfill failed');
    } finally {
      setBusy(false);
    }
  };

  const submitCert = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await axios.post(`${API}/suppliers/tds-certificates`, {
        ...certForm,
        gross_commission: parseFloat(certForm.gross_commission) || 0,
        tds_deducted: parseFloat(certForm.tds_deducted) || 0,
      }, { withCredentials: true });
      setCertForm((f) => ({ ...f, certificate_number: '', certificate_date: '', gross_commission: '', tds_deducted: '' }));
      await loadCerts();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Certificate save failed');
    } finally {
      setBusy(false);
    }
  };

  const submit26as = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await axios.post(`${API}/tds/26as/import`, null, {
        withCredentials: true,
        params: {
          fy: asForm.fy,
          quarter: asForm.quarter,
          pan: asForm.pan,
          form26as_tds_amount: parseFloat(asForm.form26as_tds_amount) || 0,
        },
      });
      await load26as();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || '26AS import failed');
    } finally {
      setBusy(false);
    }
  };

  const loadAgentStatement = async () => {
    if (!agentId.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await axios.get(`${API}/reports/agent-commission-statement/${encodeURIComponent(agentId.trim())}`, {
        withCredentials: true,
      });
      setAgentStmt(r.data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Agent statement failed');
      setAgentStmt(null);
    } finally {
      setBusy(false);
    }
  };

  const moduleOn = !!settings?.commission_tds_enabled;
  const totals = summary?.totals || {};
  const summaryRows = summary?.rows || [];
  const showEmptyStamped = tab === 'Summary' && summary && totals.bookings === 0;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="commission-tds-reports">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1B375B] flex items-center gap-2">
            <Scale className="w-7 h-7" /> Commission & TDS Reports
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            GSTR-7 prep, supplier Form 16A (194H), and 26AS TDS receivable matching.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CompliancePeriodBar
            period={period}
            onPeriodChange={setPeriod}
            fy={fy}
            onFyChange={setFy}
          />
          <button
            type="button"
            onClick={refresh}
            disabled={busy}
            className="inline-flex items-center gap-1 px-3 py-2 bg-[#1B375B] text-white text-sm font-semibold rounded-lg disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
          <button
            type="button"
            onClick={downloadCsv}
            disabled={busy}
            className="inline-flex items-center gap-1 px-3 py-2 border border-gray-200 text-sm font-semibold rounded-lg hover:bg-gray-50"
          >
            CSV
          </button>
        </div>
      </div>

      {!moduleOn && (
        <div className="mb-4 p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-900 text-sm flex gap-2" data-testid="commission-module-off-banner">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <div>
            Commission & TDS module is <strong>disabled</strong>. Enable in{' '}
            <Link to="/admin/compliance/settings" className="underline font-semibold">Compliance Settings</Link>.
          </div>
        </div>
      )}

      {err && (
        <p className="mb-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{err}</p>
      )}

      <nav className="flex flex-wrap gap-1 border-b border-gray-200 mb-6">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
              tab === t ? 'border-[#1B375B] text-[#1B375B]' : 'border-transparent text-gray-500'
            }`}
          >
            {t}
          </button>
        ))}
      </nav>

      {tab === 'Summary' && (
        <div className="space-y-6">
          {showEmptyStamped && (
            <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-900" data-testid="commission-empty-stamped">
              <p className="font-semibold">No stamped commission bookings for this period.</p>
              <p className="mt-1 text-xs">
                Overview may show fare economics from unstamped bookings. Run backfill after enabling the module.
              </p>
              {moduleOn && (
                <button
                  type="button"
                  onClick={runBackfill}
                  disabled={busy}
                  className="mt-2 px-3 py-1.5 bg-[#1B375B] text-white text-xs font-semibold rounded-lg"
                >
                  Run backfill (365 days)
                </button>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[
              ['Bookings', totals.bookings, ''],
              ['Supplier commission (gross)', totals.supplier_commission_gross, fmt],
              ['Supplier TDS receivable', totals.supplier_tds, fmt],
              ['Agent commission (gross)', totals.agent_commission_gross, fmt],
              ['Agent TDS payable', totals.agent_tds, fmt],
              ['ACF retained', totals.acf_retained, fmt],
            ].map(([label, val, fn]) => (
              <div key={label} className="bg-white border border-gray-200 rounded-xl p-4">
                <p className="text-[10px] uppercase font-bold text-gray-500">{label}</p>
                <p className="text-lg font-bold text-[#1B375B] mt-1">
                  {fn ? fn(val) : (val ?? '—')}
                </p>
              </div>
            ))}
          </div>
          <SourceBadge meta={summary?.meta} />

          {summaryRows.length > 0 && (
            <div className="overflow-x-auto bg-white border border-gray-200 rounded-xl">
              <p className="text-sm font-bold text-gray-800 p-3 border-b">Booking detail</p>
              <table className="w-full text-xs" data-testid="commission-tds-rows-table">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left p-2">Booking</th>
                    <th className="text-left p-2">PNR</th>
                    <th className="text-right p-2">Supp. gross</th>
                    <th className="text-right p-2">Supp. TDS</th>
                    <th className="text-right p-2">Agent gross</th>
                    <th className="text-right p-2">Agent TDS</th>
                    <th className="text-right p-2">ACF retained</th>
                    <th className="text-left p-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryRows.slice(0, 100).map((b) => {
                    const ca = b.commission_accounting || {};
                    return (
                      <tr key={b.booking_id} className="border-t border-gray-100">
                        <td className="p-2 font-mono">{b.booking_id}</td>
                        <td className="p-2">{b.pnr || '—'}</td>
                        <td className="p-2 text-right">{fmt(ca.supplier_commission_gross)}</td>
                        <td className="p-2 text-right">{fmt(ca.supplier_tds_amount)}</td>
                        <td className="p-2 text-right">{fmt(ca.agent_commission_gross)}</td>
                        <td className="p-2 text-right">{fmt(ca.agent_tds_amount)}</td>
                        <td className="p-2 text-right">{fmt(ca.acf_commission_retained_gross)}</td>
                        <td className="p-2 uppercase text-gray-600">{ca.status || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {summaryRows.length > 100 && (
                <p className="p-2 text-gray-500 text-xs">Showing first 100 of {summaryRows.length}</p>
              )}
            </div>
          )}

          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <p className="text-sm font-bold text-gray-800 mb-3">Agent commission statement</p>
            <div className="flex flex-wrap gap-2 mb-3">
              <input
                type="text"
                value={agentId}
                onChange={(e) => setAgentId(e.target.value)}
                placeholder="Agent user id"
                className="flex-1 min-w-[200px] px-3 py-2 border border-gray-200 rounded-lg text-sm"
                data-testid="agent-statement-id"
              />
              <button
                type="button"
                onClick={loadAgentStatement}
                disabled={busy}
                className="px-4 py-2 bg-[#659A9B] text-white text-sm font-semibold rounded-lg"
              >
                Load statement
              </button>
            </div>
            {agentStmt && (
              <div className="text-sm space-y-2" data-testid="agent-commission-statement">
                <p>
                  <strong>{agentStmt.agent?.agency_name || agentStmt.agent?.name}</strong>
                  {' '}· share {agentStmt.agent?.commission_share_pct ?? 'default'}%
                  {' '}· net payable {fmt(agentStmt.total_net_payable)}
                </p>
                <p className="text-gray-500">{agentStmt.bookings?.length || 0} bookings with commission block</p>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'Agent sharing' && (
        <AgentCommissionSharingRoster settings={settings} />
      )}

      {tab === 'GSTR-7' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => gstr7 && downloadJson(`gstr7-${fy || 'all'}.json`, gstr7)}
              disabled={!gstr7}
              className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-semibold disabled:opacity-50"
              data-testid="gstr7-download"
            >
              <Download className="w-4 h-4" /> Export JSON
            </button>
          </div>
          {!gstr7 && !busy && (
            <p className="text-sm text-gray-500">No GSTR-7 data loaded.</p>
          )}
          {gstr7 && gstr7.booking_count === 0 && (
            <div className="p-4 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-900">
              No stamped bookings for this FY. {moduleOn && (
                <button type="button" onClick={runBackfill} className="underline font-semibold ml-1">Run backfill</button>
              )}
            </div>
          )}
          {gstr7 && gstr7.booking_count > 0 && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-violet-50 rounded-xl p-4 border border-violet-100">
                  <p className="text-[10px] text-violet-700 uppercase font-bold">Bookings</p>
                  <p className="text-xl font-bold">{gstr7.booking_count}</p>
                </div>
                <div className="bg-sky-50 rounded-xl p-4 border border-sky-100">
                  <p className="text-[10px] text-sky-700 uppercase font-bold">Agent TDS payable</p>
                  <p className="text-xl font-bold">{fmt(gstr7.agent_tds_payable_total)}</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
                  <p className="text-[10px] text-emerald-700 uppercase font-bold">Supplier TDS receivable</p>
                  <p className="text-xl font-bold">{fmt(gstr7.supplier_tds_receivable_total)}</p>
                </div>
              </div>
              <SourceBadge meta={gstr7.meta} />
              <div className="overflow-x-auto bg-white border border-gray-200 rounded-xl">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left p-2">Booking</th>
                      <th className="text-left p-2">PNR</th>
                      <th className="text-right p-2">Supp. gross</th>
                      <th className="text-right p-2">Supp. TDS</th>
                      <th className="text-right p-2">Agent TDS</th>
                      <th className="text-left p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(gstr7.rows || []).slice(0, 100).map((row) => (
                      <tr key={row.booking_id} className="border-t border-gray-100">
                        <td className="p-2 font-mono">{row.booking_id}</td>
                        <td className="p-2">{row.pnr || '—'}</td>
                        <td className="p-2 text-right">{fmt(row.supplier_commission_gross)}</td>
                        <td className="p-2 text-right">{fmt(row.supplier_tds)}</td>
                        <td className="p-2 text-right">{fmt(row.agent_tds)}</td>
                        <td className="p-2 uppercase text-gray-600">{row.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(gstr7.rows || []).length > 100 && (
                  <p className="p-2 text-gray-500 text-xs">Showing first 100 of {gstr7.rows.length} — use JSON export for full file.</p>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {tab === 'Form 16A' && (
        <div className="grid md:grid-cols-2 gap-6">
          <form onSubmit={submitCert} className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
            <p className="text-sm font-bold text-[#1B375B] flex items-center gap-1">
              <FileText className="w-4 h-4" /> Record Form 16A / TDS certificate
            </p>
            <select
              value={certForm.supplier}
              onChange={(e) => setCertForm({ ...certForm, supplier: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg text-sm"
            >
              {SUPPLIERS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-2">
              <input className="px-3 py-2 border rounded-lg text-sm" placeholder="FY" value={certForm.fy} onChange={(e) => setCertForm({ ...certForm, fy: e.target.value })} />
              <input className="px-3 py-2 border rounded-lg text-sm" placeholder="Quarter Q1" value={certForm.quarter} onChange={(e) => setCertForm({ ...certForm, quarter: e.target.value })} />
            </div>
            <input className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Certificate no." value={certForm.certificate_number} onChange={(e) => setCertForm({ ...certForm, certificate_number: e.target.value })} required />
            <input type="date" className="w-full px-3 py-2 border rounded-lg text-sm" value={certForm.certificate_date} onChange={(e) => setCertForm({ ...certForm, certificate_date: e.target.value })} required />
            <input className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Gross commission ₹" value={certForm.gross_commission} onChange={(e) => setCertForm({ ...certForm, gross_commission: e.target.value })} />
            <input className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="TDS deducted ₹" value={certForm.tds_deducted} onChange={(e) => setCertForm({ ...certForm, tds_deducted: e.target.value })} />
            <input className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Supplier PAN" value={certForm.pan} onChange={(e) => setCertForm({ ...certForm, pan: e.target.value })} />
            <button type="submit" disabled={busy} className="w-full py-2 bg-[#1B375B] text-white text-sm font-semibold rounded-lg">Save certificate</button>
          </form>
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-sm font-semibold text-gray-700">List by supplier</span>
              <select value={certSupplier} onChange={(e) => setCertSupplier(e.target.value)} className="px-2 py-1 border rounded text-sm">
                {SUPPLIERS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {certs.length === 0 && <p className="text-sm text-gray-500">No certificates recorded.</p>}
              {certs.map((c) => (
                <div key={c.id} className="bg-white border border-gray-200 rounded-lg p-3 text-xs">
                  <p className="font-bold">{c.supplier} · {c.fy} {c.quarter}</p>
                  <p className="text-gray-600">Cert {c.certificate_number} · {c.certificate_date}</p>
                  <p>Gross {fmt(c.gross_commission)} · TDS {fmt(c.tds_deducted)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === '26AS match' && (
        <div className="grid md:grid-cols-2 gap-6">
          <form onSubmit={submit26as} className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
            <p className="text-sm font-bold text-[#1B375B] flex items-center gap-1">
              <Receipt className="w-4 h-4" /> Import 26AS TDS (Sec 194H receivable)
            </p>
            <input className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="FY" value={asForm.fy} onChange={(e) => setAsForm({ ...asForm, fy: e.target.value })} />
            <input className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Quarter" value={asForm.quarter} onChange={(e) => setAsForm({ ...asForm, quarter: e.target.value })} />
            <input className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Deductor PAN" value={asForm.pan} onChange={(e) => setAsForm({ ...asForm, pan: e.target.value })} required />
            <input className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="26AS TDS amount ₹" value={asForm.form26as_tds_amount} onChange={(e) => setAsForm({ ...asForm, form26as_tds_amount: e.target.value })} required />
            <button type="submit" disabled={busy} className="w-full py-2 bg-[#1B375B] text-white text-sm font-semibold rounded-lg">Match against books</button>
            <p className="text-[10px] text-gray-500">Compares Form 26AS figure to supplier TDS receivable ledger (account 1200).</p>
          </form>
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {recon26.length === 0 && <p className="text-sm text-gray-500">No reconciliation runs yet.</p>}
            {recon26.map((r) => (
              <div
                key={r.id}
                className={`border rounded-lg p-3 text-xs ${
                  r.status === 'matched' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
                }`}
                data-testid="26as-recon-row"
              >
                <div className="flex items-center gap-1 font-bold">
                  {r.status === 'matched' ? <CheckCircle2 className="w-3 h-3 text-emerald-700" /> : <AlertTriangle className="w-3 h-3 text-amber-700" />}
                  {r.pan} · {r.fy} {r.quarter}
                </div>
                <p>26AS {fmt(r.form26as_tds_amount)} · Books {fmt(r.books_tds_receivable)} · Var {fmt(r.variance)}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
